import { For, Show, type Component } from 'solid-js';

import type { PlanStatus, PlanTier } from '../lib/types.js';

/** "25 companies" — the number a merchant checks before they hit a wall. */
function tierCapacity(tier: PlanTier): string {
  return tier.activeCompanies === null ? 'Unlimited' : `${tier.activeCompanies}`;
}

/** What a tier adds, in the merchant's words rather than field names. */
function tierIncludes(tier: PlanTier): string {
  const features = [
    tier.includes.automatedDunning ? 'Payment reminders' : null,
    tier.includes.statements ? 'Monthly statements' : null,
    tier.includes.buyerPortal ? 'Buyer portal' : null,
    tier.includes.agingExports ? 'CSV exports' : null,
    tier.includes.manualInvoices ? 'Manual invoices' : null,
  ].filter((feature): feature is string => feature !== null);

  return features.length === 0 ? 'Credit limits and the aging dashboard' : features.join(' · ');
}

function price(tier: PlanTier): string {
  return tier.monthlyPriceUsd === 0 ? 'Free' : `$${tier.monthlyPriceUsd}/mo`;
}

/**
 * Plan and limits (SPEC FR-11, NFR N5 "no surprise paywalls").
 *
 * Rendered as a real `s-table`, not the stack of `s-text` rows this replaced. Those rows relied on
 * `emphasis="bold"` to mark the current plan — an attribute `s-text` does not have — so every tier
 * looked identical and the comparison a merchant is meant to make was impossible to make. A table
 * also aligns price against capacity against features, which is the whole job of this panel.
 *
 * Deliberately always visible rather than a screen you navigate to: a merchant should never meet a
 * limit for the first time in an error message.
 */
export const PlanAndLimits: Component<{ plan: PlanStatus }> = (props) => {
  const usage = (): string => {
    if (props.plan.capacity === null) {
      return `${props.plan.plan} plan · unlimited companies`;
    }
    return `${props.plan.plan} plan · ${Math.min(props.plan.used, props.plan.capacity)} of ${props.plan.capacity} companies included`;
  };

  return (
    <s-section heading="Plan and limits">
      <s-stack direction="block" gap="base">
        <s-text>{usage()}</s-text>

        <Show when={props.plan.nearingLimit}>
          <s-banner tone="warning">
            <s-paragraph>
              You are close to the company limit on the {props.plan.plan} plan. Nothing stops
              working when you reach it — your ledger keeps updating and existing credit limits keep
              enforcing. Only setting a limit on a NEW company is paused until you upgrade.
            </s-paragraph>
          </s-banner>
        </Show>

        <s-table>
          <s-table-header-row>
            <s-table-header>Plan</s-table-header>
            <s-table-header>Price</s-table-header>
            <s-table-header>Companies</s-table-header>
            <s-table-header>Includes</s-table-header>
          </s-table-header-row>
          <s-table-body>
            <For each={props.plan.tiers}>
              {(tier) => (
                <s-table-row>
                  <s-table-cell>
                    <s-stack direction="inline" gap="small" alignItems="center">
                      <s-text>{tier.plan}</s-text>
                      <Show when={tier.current}>
                        <s-badge tone="info">Current</s-badge>
                      </Show>
                    </s-stack>
                  </s-table-cell>
                  <s-table-cell>
                    <s-text fontVariantNumeric="tabular-nums">{price(tier)}</s-text>
                  </s-table-cell>
                  <s-table-cell>
                    <s-text fontVariantNumeric="tabular-nums">{tierCapacity(tier)}</s-text>
                  </s-table-cell>
                  <s-table-cell>
                    <s-text tone="subdued">{tierIncludes(tier)}</s-text>
                  </s-table-cell>
                </s-table-row>
              )}
            </For>
          </s-table-body>
        </s-table>
      </s-stack>
    </s-section>
  );
};
